import { Dispatch, Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { loadAndTrack } from "redux-request-loading";

import { beginAjaxCall, Payload } from "@neworbit/redux-helpers";

import { orgStructureApi } from "@teamprevent/common/questionnaires/components/referralForm/OrgStructureApi";
import { HrRepresentative } from "@teamprevent/common/hrRepresentatives/models";
import { toast } from "@teamprevent/common/toasts";

import { LineManager } from "../../../lineManagers/models";
import { RecruitmentManager } from "../../../recruitmentManagers/models";

import { AnsweredQuestionWithId, OrgLevel } from "../../model";

import { LoadedQuestionnairePayload, questionnaireLoaded, submitInvalidQuestionnaire, submitQuestionnaireSuccess } from "../../actions";

import HrRepresentativesApi from "./HrRepresentativesApi";
import * as actions from "./actionTypes";
import { AUTO_SAVE_REFERRAL_FORM, AUTO_SAVE_REFERRAL_FORM_SUCCESS, SUBMIT_REFERRAL_FORM } from "./actionTypes";
import { REFERRAL_QUESTIONNAIRE_KEY } from "./ReferralForm";
import { referralApi } from "./referralApi";
import { LoadReferralRequestModel } from "./models";

export type ReferralFormAction =
    ({ type: typeof actions.LOAD_HR_REPRESENTATIVES_SUCCESS } & Payload<HrRepresentative[]>) |
    ({ type: typeof actions.LOAD_LINE_MANAGERS_SUCCESS } & Payload<LineManager[]>) |
    ({ type: typeof actions.LOAD_ORG_STRUCTURES_SUCCESS } & Payload<OrgLevel[]>) |
    ({ type: typeof actions.CLEAR_ORG_STRUCTURES } & Payload<OrgLevel[]>) |
    ({ type: typeof actions.LOAD_RECRUITMENT_MANAGERS_SUCCESS } & Payload<RecruitmentManager[]>);

export const loadHrRepresentativesSuccess = (payload: HrRepresentative[]): ReferralFormAction => ({
    payload,
    type: actions.LOAD_HR_REPRESENTATIVES_SUCCESS
});

export const loadLineManagersSuccess = (payload: LineManager[]): ReferralFormAction => ({
    payload,
    type: actions.LOAD_LINE_MANAGERS_SUCCESS
});

export const loadRecruitmentManagersSuccess = (payload: RecruitmentManager[]): ReferralFormAction => ({
    payload,
    type: actions.LOAD_RECRUITMENT_MANAGERS_SUCCESS
});

export const loadOrgStructureSuccess = (payload: OrgLevel[]): ReferralFormAction => ({
    payload,
    type: actions.LOAD_ORG_STRUCTURES_SUCCESS
});

export const clearOrgStructureSuccess = (): ReferralFormAction => ({
    payload: [],
    type: actions.CLEAR_ORG_STRUCTURES
});

export function loadHrRepresentatives() {
    return async (dispatch: Dispatch) => {
        const loadHrRepresentativesPromise = loadAndTrack(dispatch, "loadHrRepresentatives", HrRepresentativesApi.getAll());
        const hrRepresentatives = await loadHrRepresentativesPromise;
        return dispatch(loadHrRepresentativesSuccess(hrRepresentatives));
    };
}

export function loadOrgStructure(parentId?: string) {
    return async (dispatch: Dispatch) => {
        const orgLevels = await loadAndTrack(dispatch, "loadOrgStructures", orgStructureApi.getAll(parentId));
        dispatch(loadOrgStructureSuccess(orgLevels));
    };
}

export function clearReferralFormOrgStructures() {
    return (dispatch: Dispatch) => {
        dispatch(clearOrgStructureSuccess());
    };
}

export function submitReferralQuestionnaire(questions: AnsweredQuestionWithId[], apiUrl: string, referAgainOhCaseId: string | undefined):
    ThunkAction<any, any, undefined, Action> {
    if (!questions.every(question => question.isValid)) {
        return (dispatch: Dispatch) => dispatch(submitInvalidQuestionnaire(REFERRAL_QUESTIONNAIRE_KEY, true));
    }
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, SUBMIT_REFERRAL_FORM, referralApi.submitReferral(questions, apiUrl, referAgainOhCaseId));
        toast.success("Referral submitted successfully");
        dispatch(submitQuestionnaireSuccess(REFERRAL_QUESTIONNAIRE_KEY));
        return response.data;
    };
}

export const saveReferralQuestionnaire = (
    questions: AnsweredQuestionWithId[],
    eTag: string | null | undefined,
    isInitial: boolean,
    referAgainOhCaseId: string | undefined
) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(
            dispatch,
            AUTO_SAVE_REFERRAL_FORM,
            referralApi.saveReferral(questions, eTag, isInitial, referAgainOhCaseId)
        );
        dispatch(saveReferralSuccess({ questionnaireKey: REFERRAL_QUESTIONNAIRE_KEY, questions: response.data.questions, eTag: response.eTag, isInitial }));
    };
};

export function loadReferralQuestionnaire(requestModel: LoadReferralRequestModel, abortController?: AbortController) {
    return async (dispatch: Dispatch) => {
        dispatch(beginAjaxCall("loadQuestionnaire"));
        const response = (await referralApi.loadReferral(requestModel, abortController)).data;
        const questions = response.data.questions as AnsweredQuestionWithId[];
        const referralSubmittedDate = response.referralSubmittedDate;
        dispatch(questionnaireLoaded({ questions, questionnaireKey: REFERRAL_QUESTIONNAIRE_KEY, referralSubmittedDate }));
    };
}

export const saveReferralSuccess = (payload: LoadedQuestionnairePayload & { isInitial: boolean }) => {
    return {
        type: AUTO_SAVE_REFERRAL_FORM_SUCCESS,
        payload
    };
};
